import { useEffect, useRef } from 'react'
// Subpath, NOT the '@edge/charts' barrel — this hook mounts eagerly (auth
// provider tree) and the barrel drags the klinecharts runtime into the entry
// bundle (see the '@edge/charts/interaction' precedent in vite.config.ts).
import { DEFAULT_INTERACTION_SETTINGS } from '@edge/charts/interaction'
import type { ChartInteractionSettings } from '@edge/charts/interaction'
import { sanitizeResetZoomPolicy } from '@edge/charts/reset-zoom'
import { useChartInteraction } from '@/context/chart-interaction-hooks'
import { useAccountStore } from '@/stores/account-store'
import { useUpdateUserMutation } from '@/services/api/users-api'

/**
 * Two-way sync of the global chart interaction settings with the user's
 * server profile (`chartInteraction`), so a chosen wheel/drag grammar
 * follows the user across devices. localStorage stays the fast local cache
 * in `ChartInteractionContext`; this layers the server on top.
 *
 * - On first profile load: apply the server value (server wins on login).
 *   Only keys that already exist on `DEFAULT_INTERACTION_SETTINGS`, with a
 *   matching value type, plus the allow-listed optional object keys after
 *   their sanitizer (`OPTIONAL_OBJECT_KEYS`), are applied. Garbage on the
 *   server record is ignored rather than crashing the sync.
 * - On later local changes: push the new settings object up (deduped via
 *   JSON.stringify comparison).
 * - A user with no saved value triggers NO write on login — `lastSynced`
 *   seeds to the current (merged) settings, so only a genuine change syncs up.
 *
 * Mount once inside the authenticated tree (see AppLayout, next to
 * `useThemeServerSync`).
 */
export function useChartInteractionServerSync(): void {
  const { interaction, update } = useChartInteraction()
  const user = useAccountStore((s) => s.user)
  const [updateUser] = useUpdateUserMutation()
  const initialized = useRef(false)
  const lastSynced = useRef<string | null>(null)

  useEffect(() => {
    if (!user) return

    // First profile load: apply the server value; seed lastSynced so a user
    // with no saved value doesn't trigger a write. Return BEFORE the push
    // path — `update` re-runs this effect with the merged interaction, and
    // this commit's `interaction` is still stale.
    if (!initialized.current) {
      initialized.current = true
      const serverPatch = validateServerValue(user.chartInteraction)
      const merged = serverPatch ? { ...interaction, ...serverPatch } : interaction
      lastSynced.current = JSON.stringify(merged)
      if (serverPatch && JSON.stringify(merged) !== JSON.stringify(interaction)) {
        update(serverPatch)
      }
      return
    }

    // Later local changes: push the new settings object up (deduped).
    const serialized = JSON.stringify(interaction)
    if (serialized !== lastSynced.current) {
      lastSynced.current = serialized
      void updateUser({ chartInteraction: { ...interaction } })
    }
  }, [user, interaction, update, updateUser])
}

type VisibleWindow = NonNullable<ChartInteractionSettings['historyVisibleWindow']>

/** Shape check for the anchored-chart visible window: two finite minute
 *  bounds, optional integer day offsets. Returns a fresh object carrying only
 *  those fields, so junk keys never survive a round trip. */
function sanitizeVisibleWindow(raw: unknown): VisibleWindow | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined
  const { startMin, endMin, startDayOffset, endDayOffset } = raw as Record<string, unknown>
  if (typeof startMin !== 'number' || !Number.isFinite(startMin)) return undefined
  if (typeof endMin !== 'number' || !Number.isFinite(endMin)) return undefined
  if (startDayOffset !== undefined && !Number.isInteger(startDayOffset)) return undefined
  if (endDayOffset !== undefined && !Number.isInteger(endDayOffset)) return undefined
  const clean: VisibleWindow = { startMin, endMin }
  if (startDayOffset !== undefined) clean.startDayOffset = startDayOffset as number
  if (endDayOffset !== undefined) clean.endDayOffset = endDayOffset as number
  return clean
}

/** Optional object-valued keys that have no entry on the defaults (so the
 *  primitive loop below never sees them), each with its own sanitizer. A
 *  sanitizer returns undefined for anything unusable and the key is skipped. */
const OPTIONAL_OBJECT_KEYS = {
  resetZoom: sanitizeResetZoomPolicy,
  historyVisibleWindow: sanitizeVisibleWindow,
} as const satisfies {
  [K in keyof ChartInteractionSettings]?: (raw: unknown) => ChartInteractionSettings[K]
}

/** Loose validation: keep only keys that exist on the default settings and
 *  whose value has the same primitive type, plus the allow-listed optional
 *  object keys after their sanitizer. Returns null when nothing valid is
 *  present. */
function validateServerValue(
  raw: Record<string, unknown> | null | undefined,
): Partial<ChartInteractionSettings> | null {
  if (!raw) return null
  const patch: Partial<ChartInteractionSettings> = {}
  let hasAny = false
  for (const key of Object.keys(DEFAULT_INTERACTION_SETTINGS) as Array<
    keyof ChartInteractionSettings
  >) {
    const value = raw[key]
    if (typeof value === typeof DEFAULT_INTERACTION_SETTINGS[key]) {
      ;(patch as Record<string, unknown>)[key] = value
      hasAny = true
    }
  }
  for (const [key, sanitize] of Object.entries(OPTIONAL_OBJECT_KEYS)) {
    const clean = sanitize(raw[key])
    if (clean) {
      ;(patch as Record<string, unknown>)[key] = clean
      hasAny = true
    }
  }
  return hasAny ? patch : null
}
